import { rootApi } from "./baseApi";
import type { Zone } from "../types";
import { normalizeZoneAssignedUserIds } from "../utils/zoneUserIds";

interface CreateZoneRequest {
  zoneName: string;
}

interface UpdateZoneRequest {
  zoneId: number;
  zoneName: string;
}

interface AssignSensorRequest {
  sensorId: number;
}

interface ZoneSensor {
  sensor_id: number;
  sensor_name: string;
  sensor_serialNo: number | null;
}

// Define valid tag types
type TagTypes = 'Sensors' | 'BaseUnits' | 'LiveData' | 'DayData' | 'Zones' | 'ZoneSensors' | 'UserZone';

export const zoneApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getZones: builder.query<Zone[], void>({
      query: () => ({
        url: "/zones",
        method: "GET"
      }),
      // Refresh zones every 5 minutes
      keepUnusedDataFor: 300, // 5 minutes in seconds
      transformResponse: (response: any) => {
        if (!Array.isArray(response)) {
          console.warn('Invalid zones response:', response);
          return [];
        }
        return response.map((zone: any) => {
          let zu: unknown = zone.zone_users;
          if (typeof zu === "string") {
            try {
              zu = zu ? JSON.parse(zu) : [];
            } catch {
              zu = [];
            }
          }
          return {
            zone_id: Number(zone.zone_id),
            zone_name: zone.zone_name,
            is_deleted: zone.is_deleted,
            sensor_count: Number(zone.sensor_count ?? 0),
            description: zone.description,
            zone_users: normalizeZoneAssignedUserIds(zu ?? []),
          };
        });
      },
      providesTags: ['Zones'],
    }),

    getZoneSensors: builder.query<ZoneSensor[], number>({
      query: (zoneId) => ({
        url: `/zones/${zoneId}/sensors`,
        method: "GET"
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: any) => {
        if (!Array.isArray(response)) {
          console.warn('Invalid zone sensors response:', response);
          return [];
        }
        return response.map(sensor => ({
          sensor_id: sensor.sensor_id,
          sensor_name: sensor.sensor_name,
          sensor_serialNo: sensor.sensor_serialNo ?? null,
        }));
      },
      providesTags: (result, error, zoneId) => [{ type: 'ZoneSensors', id: zoneId }],
    }),

    createZone: builder.mutation<Zone, CreateZoneRequest>({
      query: (newZone) => ({
        url: "/zones",
        method: "POST",
        body: {
          zoneName: newZone.zoneName
        },
      }),
      async onQueryStarted({ zoneName }, { dispatch, queryFulfilled }) {
        try {
          const { data: newZone } = await queryFulfilled;
          if (newZone?.zone_id) {
            dispatch(
              zoneApi.util.updateQueryData('getZones', undefined, (draft) => {
                if (!draft.some(z => z.zone_id === newZone.zone_id)) {
                  draft.push({
                    zone_id: newZone.zone_id,
                    zone_name: zoneName,
                    sensor_count: 0,
                    zone_users: [],
                    is_deleted: false,
                    description: '',
                  });
                }
              })
            );
          }
        } catch {}
      },
    }),

    updateZone: builder.mutation<Zone, UpdateZoneRequest>({
      query: ({ zoneId, zoneName }) => ({
        url: `/zones`,
        method: "PATCH",
        body: { zoneId, zoneName },
      }),
      invalidatesTags: ['Zones'],
    }),

    deleteZone: builder.mutation<void, number>({
      query: (zoneId) => ({
        url: `/zones/${zoneId}`,
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      invalidatesTags: (result, error, zoneId) => [
        { type: 'ZoneSensors', id: zoneId }
      ],
      async onQueryStarted(zoneId, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          zoneApi.util.updateQueryData('getZones', undefined, (draft) => {
            const index = draft.findIndex(z => z.zone_id === zoneId);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    }),

    assignSensor: builder.mutation<void, { zoneId: number; sensorId: number; sensorName?: string; sensorSerialNo?: number | string }>({
      query: ({ zoneId, sensorId }) => ({
        url: `/zones/${zoneId}/sensors`,
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: {
          sensorId: sensorId
        }
      }),
      async onQueryStarted({ zoneId, sensorId, sensorName, sensorSerialNo }, { dispatch, queryFulfilled }) {
        const zid = Number(zoneId);
        const sid = Number(sensorId);

        const patch = dispatch(
          zoneApi.util.updateQueryData('getZoneSensors', zid, (draft) => {
            if (!draft.some(s => Number(s.sensor_id) === sid)) {
              draft.push({
                sensor_id: sid,
                sensor_name: sensorName ?? '',
                sensor_serialNo: sensorSerialNo != null ? Number(sensorSerialNo) : null,
              });
            }
          })
        );
        const zonesPatch = dispatch(
          zoneApi.util.updateQueryData('getZones', undefined, (draft) => {
            const zone = draft.find(z => Number(z.zone_id) === zid);
            if (zone) zone.sensor_count = Number(zone.sensor_count ?? 0) + 1;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          zonesPatch.undo();
        }
      },
      // No invalidatesTags: refetching GET /zones can race (out-of-order responses) and
      // overwrite optimistic sensor_count. Cache is updated in onQueryStarted above.
    }),

    removeSensor: builder.mutation<void, { zoneId: number; sensorId: number }>({
      query: ({ zoneId, sensorId }) => ({
        url: `/zones/${zoneId}/sensors/${sensorId}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      async onQueryStarted({ zoneId, sensorId }, { dispatch, queryFulfilled }) {
        const zid = Number(zoneId);
        const sid = Number(sensorId);

        const patch = dispatch(
          zoneApi.util.updateQueryData('getZoneSensors', zid, (draft) => {
            const idx = draft.findIndex(s => Number(s.sensor_id) === sid);
            if (idx !== -1) draft.splice(idx, 1);
          })
        );
        const zonesPatch = dispatch(
          zoneApi.util.updateQueryData('getZones', undefined, (draft) => {
            const zone = draft.find(z => Number(z.zone_id) === zid);
            if (zone) {
              const n = Number(zone.sensor_count ?? 0);
              if (n > 0) zone.sensor_count = n - 1;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          zonesPatch.undo();
        }
      },
      // No invalidatesTags — same reasoning as assignSensor (avoid stale GET /zones).
    }),

    // Get zones accessible to current user
    getUserZones: builder.query<Zone[], void>({
      query: () => ({
        url: '/zones/user',
        method: 'GET'
      }),
      providesTags: [{ type: 'UserZone' as TagTypes }]
    }),

    // Assign user to zone (admin only)
    assignUserToZone: builder.mutation<void, { zoneId: number, userId: number }>({
      query: ({ zoneId, userId }) => ({
        url: `/zones/${zoneId}/users`,
        method: 'POST',
        body: { userId }
      }),
      invalidatesTags: [{ type: 'UserZone' as TagTypes }]
    }),

    // Remove user from zone (admin only)
    removeUserFromZone: builder.mutation<void, { zoneId: number, userId: number }>({
      query: ({ zoneId, userId }) => ({
        url: `/zones/${zoneId}/users/${userId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'UserZone' as TagTypes }]
    }),

    updateZoneUsers: builder.mutation<void, { zoneId: number; userIds: number[] }>({
      query: ({ zoneId, userIds }) => ({
        url: `/zones/${zoneId}/users`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { userIds },
      }),
      invalidatesTags: ['UserZone'],
      async onQueryStarted({ zoneId, userIds }, { dispatch, queryFulfilled }) {
        const zid = Number(zoneId);
        const normalized = normalizeZoneAssignedUserIds(userIds);
        const patch = dispatch(
          zoneApi.util.updateQueryData('getZones', undefined, (draft) => {
            const zone = draft.find((z) => Number(z.zone_id) === zid);
            if (zone) {
              zone.zone_users = normalized;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetZonesQuery,
  useGetZoneSensorsQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useAssignSensorMutation,
  useRemoveSensorMutation,
  useGetUserZonesQuery,
  useAssignUserToZoneMutation,
  useRemoveUserFromZoneMutation,
  useUpdateZoneUsersMutation,
} = zoneApi;